import type { EditorDraft, EditorKnowledge } from '@/features/editor/types/editor.types'
import type { KnowledgeEntry } from '@/features/editor/types/knowledge.types'
import { normalizeTags } from '@/features/editor/lib/editor-tags'

function normalizeKeywords(xs: string[]): string[] {
  return Array.isArray(xs)
    ? xs
        .map((x) => (typeof x === 'string' ? x.trim() : ''))
        .filter((x) => x.length > 0)
    : []
}

function normalizeKeywordLogic(v: unknown): 'and' | 'or' {
  return v === 'and' ? 'and' : 'or'
}

/**
 * 仅保留“保存语义”字段：
 * - 对齐 buildUpdateRequest（你真正会提交给后端的那部分）
 * - 排除：engineId、promptTemplateMode（后端未支持）、publishStatus、keywordOp 等
 */
function pickEntryForDigest(
  e: KnowledgeEntry,
  opts?: { includeUserCard?: boolean }
) {
  const includeUserCard = opts?.includeUserCard ?? false

  if (e.retrievalMethod === 'prompt') {
    const payload = {
      id: e.id,
      title: e.title,
      content: e.content,
      enabled: e.enabled,
      retrievalMethod: 'prompt' as const,
      keywords: normalizeKeywords(e.keywords),
      keywordLogic: normalizeKeywordLogic(e.keywordOp),
    }
    return includeUserCard ? { ...payload, isUserCard: e.isUserCard } : payload
  }

  const payload = {
    id: e.id,
    title: e.title,
    content: e.content,
    enabled: e.enabled,
    retrievalMethod: 'permanent' as const,
  }
  return includeUserCard ? { ...payload, isUserCard: e.isUserCard } : payload
}

/**
 * digest 输入 = “如果现在点保存，后端会看到什么”
 * 给 digestEditor 用；store 的 markSaved/computeDigest 和 selector 的 selectIsDirty 必须共用它。
 */
export function buildDigestInput(draft: EditorDraft, knowledge: EditorKnowledge) {
  return {
    draft: {
      title: draft.title,
      description: draft.description,
      tags: normalizeTags(draft.tags),
      coverImageUrl: draft.coverImageUrl,
      backgroundImageUrl: draft.backgroundImageUrl,
      narrativeCore: draft.narrativeCore,
      firstMessage: draft.firstMessage,
    },
    knowledge: {
      worldInfo: (knowledge.worldInfo ?? []).map((entry) =>
        pickEntryForDigest(entry)
      ),
      characterProfiles: (knowledge.characterProfiles ?? []).map((entry) =>
        pickEntryForDigest(entry, { includeUserCard: true })
      ),
    },
  }
}

export type EditorDigestInput = ReturnType<typeof buildDigestInput>
